import {isEmpty} from 'lodash';
import {
	entities,
	formBodyType,
	itApplicationFilter,
	itControlApproachType,
	controlDesignEffectivenessTypeId
} from '../../../../../../../util/uiconstants';
import {isNullOrUndefined} from '../../../../../../../util/utils';

const BodyTypeFiltersConfig = {
	[formBodyType.ISA315ScotItApplication]: {
		filterITApplicationType: itApplicationFilter.itApplication
	},
	[formBodyType.ISA315ItProcessItRisk]: {
		filterIsIPEManuallyTested: false,
		filterITApplicationType: itApplicationFilter.itApplication,
		filterItApproachType: itControlApproachType.controls,
		filterControlDesignEffectiveness: [
			controlDesignEffectivenessTypeId.Null,
			controlDesignEffectivenessTypeId.Effectiveness,
			0 /* The API returns this instead of null */
		]
	},
	[formBodyType.ISA315ItGCTestStrategy]: {
		filterIsIPEManuallyTested: false,
		filterControlHasTesting: true,
		filterItApproachType: itControlApproachType.controls
	}
};

export function getClientFiltersForBodyType(formBodyTypeId) {
	return {
		...BodyTypeFiltersConfig[formBodyTypeId]
	};
}

// ISA 315 Client-side filters declaration
const FilterConfiguration = {
	filterIsIPEManuallyTested: {
		entityId: entities.Control,
		filterFunction: (item, filterValue) => {
			const filterIsEmpty = isNullOrUndefined(filterValue);
			return filterIsEmpty || item.isIPEManuallyTested === filterValue;
		}
	},
	filterControlHasTesting: {
		entityId: entities.Control,
		filterFunction: (item, filterValue) => {
			const filterIsEmpty = isNullOrUndefined(filterValue);
			return filterIsEmpty || item.hasControlTesting === filterValue;
		}
	},
	filterControlDesignEffectiveness: {
		entityId: entities.Control,
		filterFunction: (item, filterValue) => {
			const filterIsEmpty = isNullOrUndefined(filterValue);
			if (filterIsEmpty) {
				return true;
			} else {
				const controlDesignEffectivenessList = Array.isArray(filterValue) ? filterValue : [filterValue];
				return controlDesignEffectivenessList.includes(item.controlDesignEffectivenessTypeId);
			}
		}
	},
	filterITApplicationType: {
		entityId: entities.ItSoApplication,
		filterFunction: (item, filterValue) => {
			const filterIsEmpty = isNullOrUndefined(filterValue);
			const isServiceOrgFilter = filterValue === 1 ? false : true;

			// null | undefined -> Filter does not apply (return true)
			// 1 -> true if item is an itApplication (isServiceOrganization === false)
			// anything else -> true if item is a service org (isServiceOrganization === true)
			return filterIsEmpty || item.isServiceOrganization === isServiceOrgFilter;
		}
	},
	filterItApproachType: {
		entityId: entities.ItControl,
		filterFunction: (item, filterValue) => {
			const filterIsEmpty = isNullOrUndefined(filterValue);

			return filterIsEmpty || item.itApproachTypeId === filterValue;
		}
	},
	filterControlType: {
		entityId: entities.Control,
		filterFunction: (item, filterValue) => {
			const filterIsEmpty = isNullOrUndefined(filterValue);
			if (filterIsEmpty) {
				return true;
			} else {
				const controlTypeIdList = Array.isArray(filterValue) ? filterValue : [filterValue];
				return controlTypeIdList.includes(item.controlTypeId);
			}
		}
	}
};

export function getFiltersForEntityType(entityId, clientFilters) {
	let entityFilters = {};
	for (const clientFilterName of Object.keys(clientFilters)) {
		const filterConfig = FilterConfiguration[clientFilterName];
		if (filterConfig && filterConfig.entityId === entityId) {
			entityFilters[clientFilterName] = filterConfig.filterFunction;
		}
	}

	return entityFilters;
}

function executeFilters(item, entityFilters, filterValues) {
	let matchFilter = true;

	if (!item) {
		return false;
	}

	// No filters are being applied.
	if (!entityFilters || !filterValues) {
		return true;
	}

	for (const filter of Object.entries(entityFilters)) {
		const [filterName, filterFunction] = filter;
		matchFilter = filterFunction(item, filterValues[filterName]);
		if (!matchFilter) {
			break;
		}
	}

	return matchFilter;
}

/**
 * Applies filters for a given entityUid list.
 * @param {number} entityId Entity type that the filters will be applied to.
 * @param {object} clientFilters Filters to be applied on the entities that are on entityUidList.
 * @param {Array} entityUidList Array with a list of entity UIDs (example ControlId's).
 * @param {object} entitiesById byId slice containing the entities details.
 * @returns {Array} Array with the filtered entity Ids.
 */
export function applyFilters(entityId, clientFilters, entityUidList, entitiesById) {
	let filteredEntityIds = [];

	if (isNullOrUndefined(entityId) || !entitiesById) {
		return null;
	}

	if (isEmpty(clientFilters)) {
		return entityUidList;
	}

	// If no entityUidList is passed, apply filters on all the items in entitiesById
	if (!entityUidList) {
		entityUidList = Object.keys(entitiesById);
	}

	// Get the filters to be applied on the items list and are configured for the given entityId.
	let entityFilters = getFiltersForEntityType(entityId, clientFilters);

	// Iterate through entityUidList and keep only the ones that match all the filters in entityFilters.
	let matchFilter;
	for (const entityUid of entityUidList) {
		const entityDetails = entitiesById[entityUid];
		matchFilter = executeFilters(entityDetails, entityFilters, clientFilters);

		if (matchFilter) {
			filteredEntityIds.push(entityDetails.id);
		}
	}

	return filteredEntityIds;
}
